/**
 * ACCOUNT-OPTIONS.MODAL
 * Edit account options
 */

import Link from "@/components/link"
import Modal from "@/components/modal"
import ModalConfirm from "@/components/modal-confirm"
import Space from "@/components/space"
import { store } from "@/index"
import { useState } from "react"
import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Session } from "@/redux/_session.types"
import { filterUpdateArchive_AsSupervisor, filterUpdateDefault_AsSupervisor } from "@/redux/filter.actions"
import { surveyUpdateRpsTags, surveyUpdateTrigger } from "@/redux/survey.actions"
import { SurveyState } from "@/redux/survey.types"
import { TopicState } from "@/redux/topic.types"
import { FilterState } from "@/redux/filter.types"
import { AttributeState } from "@/redux/attribute.types"
import { flatten } from "lodash"
import ListItem from "@/components/list-item"
import Checkbox from "@/components/checkbox"
import AdmSettingsSurveyWidget from "@/widgets/adm-settings-survey.widget"
import SurveyMoveAccountModal from "@/modals/survey-move-account.modal"
import FilterCreateMergedModal from "@/modals/filter-create-merged.modal"
import Card from "@/components/card"

interface StateProps {
  _session: Session
  attribute: AttributeState
  filter: FilterState
  survey: SurveyState
  topic: TopicState
}

interface OwnProps {
  onClose: Function
}

type Props = StateProps & OwnProps & WithTranslation

const MERGE_FILTER = "MERGE_FILTER"
const MOVE_ACCOUNT = "MOVE_ACCOUNT"
const UPDATE_DEFAULT_FILTERS = "UPDATE_DEFAULT_FILTERS"
const UPDATE_FILTERS_MODAL = "UPDATE_FILTERS_MODAL"
const UPDATE_FILTER_LOADING_MODAL = "UPDATE_FILTER_LOADING_MODAL"
const UPDATE_RPS_TAGS = "UPDATE_RPS_TAGS"
const UPDATE_TRIGGER = "UPDATE_TRIGGER"

function AccountOptionsModal(props: Props) {
  const { t } = props

  const [currentModal, setCurrentModal] = useState<null | string>(null)
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([])

  function isQuestionSelected(questionId: string) {
    return selectedQuestions.indexOf(questionId) > -1
  }

  function selectQuestion(questionId: string) {
    setSelectedQuestions(
      isQuestionSelected(questionId)
        ? selectedQuestions.filter((x) => x !== questionId)
        : selectedQuestions.concat([questionId])
    )
  }

  // Capture at mount time — something inside this modal (AdmSettingsSurveyWidget)
  // dispatches FILTER_GET which resets filter.dashboard; useState initializer runs only once.
  const [selectedAttributeIds] = useState<number[]>(() =>
    flatten(props.filter.dashboard.customFilters.map((f) => f.attributesIds)).map((id) => parseInt(id, 10))
  )

  const [selectedPopulationNames] = useState<string[]>(() =>
    props.attribute.list.filter((a) => selectedAttributeIds.includes(parseInt(a.id, 10))).map((a) => a.name)
  )

  function updateDefaultFilters() {
    setCurrentModal(UPDATE_FILTER_LOADING_MODAL)
    store.dispatch(filterUpdateDefault_AsSupervisor(props.survey.active.id))
  }

  function updateFilters() {
    setCurrentModal(UPDATE_FILTER_LOADING_MODAL)
    store.dispatch(filterUpdateArchive_AsSupervisor(props.survey.active.id))
  }

  function updateRpsTags() {
    setCurrentModal(UPDATE_FILTER_LOADING_MODAL)
    store.dispatch(surveyUpdateRpsTags(props.survey.active.id))
  }

  function updateTrigger() {
    setCurrentModal(UPDATE_FILTER_LOADING_MODAL)
    store.dispatch(surveyUpdateTrigger(props.survey.active.id, selectedQuestions))
  }

  return (
    <Modal
      isCloseButtonVisible
      disableClickOutside
      title={t("account_settings_preferences_display")}
      onClose={() => props.onClose()}>
      {currentModal === MOVE_ACCOUNT && <SurveyMoveAccountModal onClose={() => setCurrentModal(null)} />}

      {currentModal === MERGE_FILTER && (
        <FilterCreateMergedModal
          attributeIds={selectedAttributeIds}
          populationNames={selectedPopulationNames}
          onClose={() => setCurrentModal(null)}
          onDone={() => {}}
        />
      )}

      {currentModal === UPDATE_FILTER_LOADING_MODAL && (
        <Modal onClose={() => setCurrentModal(null)} isCloseButtonVisible>
          {"L'opération est en cours elle peut prendre quelques minutes."}
        </Modal>
      )}

      {currentModal === UPDATE_FILTERS_MODAL && (
        <ModalConfirm
          onNo={() => setCurrentModal(null)}
          onYes={() => updateFilters()}
          text="Les filtres personnalisés vont être mis à jour à partir du dernier fichier des collaborateurs importé."
          textBold={t("utils_next_ask")}
        />
      )}

      {currentModal === UPDATE_RPS_TAGS && (
        <ModalConfirm
          onNo={() => setCurrentModal(null)}
          onYes={() => updateRpsTags()}
          text="Les tags RPS vont être mis à jour pour l'enquête"
          textBold={t("utils_next_ask")}
        />
      )}

      {currentModal === UPDATE_DEFAULT_FILTERS && (
        <ModalConfirm
          onNo={() => setCurrentModal(null)}
          onYes={() => updateDefaultFilters()}
          text="Créer des filtres personnalisés pour les filtres par défaut pour les rapports."
          textBold={t("utils_next_ask")}
        />
      )}

      {currentModal === UPDATE_TRIGGER && (
        <Modal title="Modifier un déclencheur" onClose={() => setCurrentModal(null)} onNext={() => updateTrigger()}>
          <p>
            <b>Les déclencheurs seront inversés pour les questions suivantes</b>
          </p>
          <div style={{ margin: "0px -40px" }}>
            {flatten(props.topic.list.map((x) => x.Questions.filter((x) => x.primaryId))).map((question) => (
              <ListItem key={question.id} isSmall>
                <Checkbox
                  active={isQuestionSelected(question.id)}
                  onClick={() => selectQuestion(question.id)}
                  text={question.label}
                />
                <Space />
              </ListItem>
            ))}
          </div>
        </Modal>
      )}

      <div style={{ marginLeft: "-30px" }}>
        <AdmSettingsSurveyWidget isSmall />

        {props._session.connectedAsSupervisor && (
          <Card title="Gestionnaire IDTree uniquement">
            <Link className="text-center" onClick={() => setCurrentModal(UPDATE_FILTERS_MODAL)}>
              Mettre à jour les filtres personnalisés
            </Link>
            <Link className="text-center" onClick={() => setCurrentModal(UPDATE_RPS_TAGS)}>
              Mettre à jour les tags RPS
            </Link>
            <Link className="text-center" onClick={() => setCurrentModal(UPDATE_TRIGGER)}>
              Modifier un déclencheur
            </Link>
            <Link className="text-center" onClick={() => setCurrentModal(UPDATE_DEFAULT_FILTERS)}>
              Créer des filtres personnalisés pour les filtres par défaut
            </Link>
            <Link className="text-center" onClick={() => setCurrentModal(MOVE_ACCOUNT)}>
              {"Déplacer l'enquête dans un autre compte"}
            </Link>
            <Link
              className={`text-center ${selectedAttributeIds.length < 2 ? "red-t" : ""}`}
              onClick={selectedAttributeIds.length >= 2 ? () => setCurrentModal(MERGE_FILTER) : undefined}>
              {`Fusionner les populations sélectionnées en un nouveau filtre ${selectedAttributeIds.length < 2 ? "(2 filtres minimum)" : ""}`}
            </Link>
          </Card>
        )}

        <div className="height-100" />
      </div>
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  attribute: state.attribute,
  filter: state.filter,
  survey: state.survey,
  topic: state.topic
})

export default connect(mapStateToProps)(withTranslation()(AccountOptionsModal))
